import axios from "axios";
import { isTestMode } from '../utils/testMode';
import { storage } from '../utils/storage';
import { getBackendUrl } from '../utils/backendUrl';

// Use network IP when accessed from external device, localhost otherwise

const BACKEND_API_URL = getBackendUrl();

// Development and testing configuration
const DEVELOPMENT_BYPASS = process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEV_BYPASS === 'true';

export interface CodexTokenResult {
  token: {
    address: string;
    networkId: number;
    symbol: string;
    name: string;
    info?: {
      totalSupply?: number;
    };
  };
  exchanges?: {
    address: string;
    name: string;
  }[];
  // Excitement/activity metrics
  volume5m?: number;
  change5m?: number;
  txnCount5m?: number;
  buyCount5m?: number;
  sellCount5m?: number;
}

export interface CodexFilterResponse {
  count: number;
  results: CodexTokenResult[];
}

class CodexService {
  private accessToken: string | null = null;
  private accessCodeValidated: boolean = false;

  // Validate access code and get session token
  async validateAccessCode(accessCode: string): Promise<boolean> {
    // Development bypass
    if (DEVELOPMENT_BYPASS) {
      console.log('🚧 Development bypass: Skipping access code validation');
      this.accessToken = 'dev-bypass';
      this.accessCodeValidated = true;
      return true;
    }

    try {
      const response = await axios.post(`${BACKEND_API_URL}/api/validate-access`, {
        accessCode
      });
      
      if (response.data.valid) {
        this.accessToken = accessCode; // Store the access code as token
        this.accessCodeValidated = true;
        console.log('Access code validated successfully');
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Access code validation failed:', error);
      this.accessCodeValidated = false;
      return false;
    }
  }


  // Get authorization headers
  private getAuthHeaders() {
    // Development bypass
    if (DEVELOPMENT_BYPASS) {
      return {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer dev-bypass'
      };
    }

    if (!this.accessToken) {
      throw new Error('Access code not validated. Please validate your access code first.');
    }
    
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.accessToken}`
    };
  }

  // Check if access code is validated
  isAuthenticated(): boolean {
    if (DEVELOPMENT_BYPASS) {
      return true;
    }
    
    // Test mode bypass
    if (isTestMode()) {
      return true;
    }
    
    return this.accessCodeValidated && this.accessToken !== null;
  }

  // Get usage statistics
  async getUsageStats(): Promise<any> {
    // Development bypass - return mock data
    if (DEVELOPMENT_BYPASS) {
      return {
        requestsUsed: 5,
        requestsRemaining: 995,
        gamesUsed: 1,
        gamesRemaining: 49,
        resetTime: Date.now() + (24 * 60 * 60 * 1000)
      };
    }

    try {
      const response = await axios.get(`${BACKEND_API_URL}/api/usage-stats`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get usage stats:', error);
      throw error;
    }
  }

  // Start a new game session
  async startGameSession(): Promise<any> {
    // Development bypass
    if (DEVELOPMENT_BYPASS) {
      return {
        success: true,
        message: 'Development game session started',
        remainingGames: 49
      };
    }

    try {
      const response = await axios.post(`${BACKEND_API_URL}/api/start-game`, {}, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Failed to start game session:', error);
      throw error;
    }
  }

  // Test mode bypass - skip all authentication and use dummy data
  enableTestMode(): void {
    if (isTestMode()) {
      console.log('🧪 Test mode enabled - bypassing authentication and using dummy data');
      this.accessToken = 'test-bypass';
      this.accessCodeValidated = true;
    }
  }

  // Get test dummy data for e2e testing
  async getTestGameTokens(count: number = 10): Promise<any> {
    if (!isTestMode()) {
      throw new Error('Test mode not enabled');
    }

    // Import test data utility
    const { generateTestTokenData } = await import('../utils/testData');
    
    // Return realistic dummy data that matches the expected API structure
    return {
      results: generateTestTokenData(count)
    };
  }

  // Modified getGameTokens to use test data when in test mode
  async getGameTokens(count: number = 10): Promise<any> {
    // Use test data in test mode
    if (isTestMode()) {
      return this.getTestGameTokens(count);
    }

    try {
      const response = await axios.get(`${BACKEND_API_URL}/api/game-tokens`, {
        params: { limit: count },
        headers: this.getAuthHeaders()
      });
      
      return response.data;
    } catch (error) {
      console.error('Failed to get game tokens:', error);
      
      // If authentication failed, reset the access code
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        this.accessCodeValidated = false;
        this.accessToken = null;
        throw new Error('Authentication failed. Please re-enter your access code.');
      }
      
      throw error;
    }
  }

  async getChartData(tokenAddress: string, networkId: number): Promise<any> {
    // Test mode - return dummy chart data
    if (isTestMode()) {
      console.log('🧪 Test mode: Generating dummy chart data');
      return this.getTestChartData(tokenAddress);
    }

    // Development bypass - use direct API call
    if (DEVELOPMENT_BYPASS) {
      console.log('🚧 Development bypass: Using direct Codex API call for chart data');
      return this.getChartDataDirect(tokenAddress, networkId);
    }

    try {
      const response = await axios.get(
        `${BACKEND_API_URL}/api/chart-data/${tokenAddress}/${networkId}`,
        {
          headers: this.getAuthHeaders()
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Failed to get chart data:', error);
      
      // If authentication failed, reset the access code
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        this.accessCodeValidated = false;
        this.accessToken = null;
        throw new Error('Authentication failed. Please re-enter your access code.');
      }
      
      throw error;
    }
  }

  // Generate test chart data for e2e testing
  private async getTestChartData(tokenAddress: string): Promise<any> {
    const { generateTestPriceHistory, resetTestRng, getTestRng } = await import('../utils/testData');
    
    // Use seeded RNG for consistent chart data
    resetTestRng(456); // Different seed for chart data
    const rng = getTestRng();
    
    const basePrice = rng() * 0.01 + 0.0001; // $0.0001 to $0.01
    const priceHistory = generateTestPriceHistory(basePrice, 24);
    
    // Convert to the format expected by ChartCard
    return priceHistory.map(point => ({
      timestamp: point.time,
      open: point.value * (0.98 + rng() * 0.04), // Slight variation for OHLC
      high: point.value * (1.01 + rng() * 0.02),
      low: point.value * (0.97 + rng() * 0.02),
              close: point.value,
        volume: rng() * 10000 + 1000
    }));
  }

  // ========================================
  // DEVELOPMENT BYPASS METHODS
  // ========================================

  // Direct API call for development testing
  private async getTokensDirect(limit: number = 10): Promise<CodexFilterResponse> {
    const CODEX_API_URL = "https://graph.codex.io/graphql";
    const API_KEY = process.env.REACT_APP_CODEX_API_KEY || "";

    if (!API_KEY) {
      throw new Error('REACT_APP_CODEX_API_KEY not set for development bypass');
    }

    const query = `{
      filterTokens(
        limit: ${limit}
        filters: {
          network: [1399811149]
          exchangeAddress: ["6EF8rrecthR5Dkzon8Nwu78hRvfCKubJ14M5uBEwF6P"]
        }
        rankings: [
          { attribute: volume5m, direction: DESC }
          { attribute: change5m, direction: DESC }
          { attribute: txnCount5m, direction: DESC }
        ]
      ) {
        count
        results {
          token {
            address
            networkId
            symbol
            name
            info {
              totalSupply
            }
          }
          exchanges {
            address
            name
          }
        }
      }
    }`;

    try {
      const response = await axios.post(
        CODEX_API_URL,
        { query },
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": API_KEY
          }
        }
      );

      if (response.data.errors) {
        console.error('GraphQL errors:', response.data.errors);
        throw new Error(`GraphQL errors: ${JSON.stringify(response.data.errors)}`);
      }

      const data = response.data.data;
      return {
        count: data.filterTokens.count,
        results: data.filterTokens.results
      };
    } catch (error) {
      console.error("Direct Codex API request failed:", error);
      throw error;
    }
  }

  // Direct chart data call for development testing
  private async getChartDataDirect(tokenAddress: string, networkId: number): Promise<any> {
    const CODEX_API_URL = "https://graph.codex.io/graphql";
    const API_KEY = process.env.REACT_APP_CODEX_API_KEY || "";

    if (!API_KEY) {
      throw new Error('REACT_APP_CODEX_API_KEY not set for development bypass');
    }

    const now = Math.floor(Date.now() / 1000);
    const fiveMinutesAgo = now - (5 * 60);

    const query = `{
      getBars(
        symbol: "${tokenAddress}:${networkId}"
        from: ${fiveMinutesAgo}
        to: ${now}
        resolution: "5S"
        currencyCode: "USD"
      ) {
        o
        h
        l
        c
        t
        volume
        s
      }
    }`;

    try {
      const response = await axios.post(
        CODEX_API_URL,
        { query },
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": API_KEY
          }
        }
      );

      if (response.data.errors) {
        console.error('GraphQL errors:', response.data.errors);
        throw new Error(`GraphQL errors: ${JSON.stringify(response.data.errors)}`);
      }

      const data = response.data.data;
      
      if (data.getBars.s === "no_data") {
        console.log("No chart data available for this token");
        return [];
      }
      
      // Convert to usable format
      const bars = [];
      for (let i = 0; i < data.getBars.o.length; i++) {
        const bar = {
          timestamp: data.getBars.t[i],
          open: data.getBars.o[i],
          high: data.getBars.h[i],
          low: data.getBars.l[i],
          close: data.getBars.c[i],
          volume: data.getBars.volume[i]
        };
        
        if (bar.open !== null && bar.high !== null && bar.low !== null && bar.close !== null) {
          bars.push(bar);
        }
      }
      
      return bars;
    } catch (error) {
      console.error("Direct chart data request failed:", error);
      throw error;
    }
  }

  // ========================================
  // UTILITY METHODS
  // ========================================

  // Reset authentication state
  logout(): void {
    this.accessToken = null;
    this.accessCodeValidated = false;
  }

  // Check backend health
  async checkBackendHealth(): Promise<boolean> {
    // Development bypass - always return true
    if (DEVELOPMENT_BYPASS) {
      console.log('🚧 Development bypass: Skipping backend health check');
      return true;
    }

    try {
      const response = await axios.get(`${BACKEND_API_URL}/health`, {
        timeout: 5000
      });
      return response.data.success;
    } catch (error) {
      console.error('Backend health check failed:', error);
      return false;
    }
  }

  // ========================================
  // LEGACY METHODS (for backward compatibility)
  // ========================================

  // Simple test method to verify API connection
  async testConnection(): Promise<boolean> {
    try {
      return await this.checkBackendHealth();
    } catch (error) {
      console.error('Test connection failed:', error);
      return false;
    }
  }

  // Simple test method to debug filterTokens query
  async testFilterTokens(): Promise<any> {
    try {
      const data = await this.getGameTokens(5);
      console.log('Simple filterTokens test successful:', data);
      return data;
    } catch (error) {
      console.error('Simple filterTokens test failed:', error);
      return null;
    }
  }
}

export const codexService = new CodexService();
export default CodexService; 